/**
 * UR Brand System — Phase 5b Customer Delivery + Launch Checklist.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { customerApi } from "@/lib/brandApi";

const DEFAULT_CHECKLIST = [
  "Website",
  "Social profiles",
  "Email signature",
  "Google Business Profile",
  "Signage",
  "Printed materials",
  "Launch announcement",
];

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-neutral-100">
      <header className="border-b border-neutral-800 px-4 sm:px-6 py-4 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="flex items-center max-w-5xl mx-auto">
          <div className="font-black text-orange-500 tracking-tight">Ur Designs</div>
          <div className="ml-3 text-xs text-neutral-500 hidden sm:block">Brand Delivery</div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
  );
}

export default function CustomerDelivery() {
  const { projectId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const api = useMemo(() => customerApi(token), [token]);
  const [state, setState] = useState({ loading: true, err: "", delivery: null });
  const [done, setDone] = useState({});

  useEffect(() => {
    if (!token) { setState({ loading: false, err: "unauthorized", delivery: null }); return; }
    api.get(`/${projectId}/delivery`).then((r) => {
      setState({ loading: false, err: "", delivery: r.data?.delivery || null });
    }).catch(() => setState({ loading: false, err: "unauthorized", delivery: null }));
  }, [api, projectId, token]);

  if (state.loading) return <Shell><p className="text-neutral-400">Loading…</p></Shell>;
  if (state.err === "unauthorized")
    return <Shell><p className="text-neutral-400" data-testid="delivery-unauthorized">This link is invalid or expired.</p></Shell>;
  if (!state.delivery)
    return <Shell><p className="text-neutral-400" data-testid="delivery-nothing-yet">Your brand package isn't ready yet.</p></Shell>;

  const d = state.delivery;
  const bpByLang = Object.fromEntries((d.blueprints || []).map((b) => [b.language, b.rendered_asset_url]));

  return (
    <Shell>
      <div className="text-center py-6 sm:py-10" data-testid="delivery-hero">
        <div className="text-5xl mb-6">✓</div>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight">Your Brand Is Ready</h1>
        <p className="text-neutral-400 mt-3 text-sm">Delivered {new Date(d.delivered_at).toLocaleDateString()}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8" data-testid="delivery-primary-actions">
        <a href={bpByLang.en || "#"} target="_blank" rel="noreferrer"
          data-testid="action-view-blueprint-en"
          className="block p-6 border border-neutral-800 rounded-xl hover:border-orange-500 bg-neutral-950">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">BLUEPRINT</div>
          <div className="text-xl font-black">View Brand Blueprint</div>
          <div className="text-xs text-neutral-500 mt-2">
            {bpByLang.en ? "English" : "Not yet available"}
            {bpByLang.es ? " · Spanish available" : ""}
          </div>
        </a>
        <div className="block p-6 border border-neutral-800 rounded-xl bg-neutral-950"
          data-testid="action-brand-files">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">FILES</div>
          <div className="text-xl font-black mb-3">Get Brand Files</div>
          <div className="space-y-2">
            {(d.files || []).map((f, i) => (
              <a key={i} href={f.file_url} target="_blank" rel="noreferrer"
                data-testid={`file-${f.label}`}
                className="block text-sm text-neutral-300 hover:text-orange-500">
                → {f.label}
              </a>
            ))}
            {!(d.files || []).length && <div className="text-xs text-neutral-500">No files attached</div>}
          </div>
        </div>
        <div className="block p-6 border border-neutral-800 rounded-xl bg-neutral-950"
          data-testid="action-launch-checklist">
          <div className="text-xs text-neutral-500 tracking-widest mb-2">LAUNCH</div>
          <div className="text-xl font-black mb-3">Launch Checklist</div>
          <div className="space-y-2">
            {DEFAULT_CHECKLIST.map((item) => (
              <label key={item} className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!done[item]}
                  onChange={() => setDone({ ...done, [item]: !done[item] })}
                  data-testid={`checklist-${item.toLowerCase().replace(/\s+/g, "-")}`} />
                <span className={done[item] ? "line-through text-neutral-500" : ""}>{item}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 text-center">
        <button
          data-testid="delivery-received-btn"
          className="px-6 py-3 border border-neutral-700 rounded-lg">
          I've received my brand
        </button>
      </div>
    </Shell>
  );
}
